import React, { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/browser'

import { FlagsProvider, defaultFlags } from 'constants/flags'

import useUserProfile from 'modules/user/useUserProfile'
import { useDevToolsFlags } from 'modules/devtools/useDevToolsFlags'

const formatArrayIntoObject = (flags: string[]) =>
  flags.reduce((acc, value) => ({ ...acc, [value]: true }), {})

const mapValuesToString = (flags: { [key: string]: boolean }) =>
  Object.fromEntries(Object.entries(flags).map(([k, v]) => [k, String(v)]))

const ConnectedFlagsProvider: React.FC = ({ children }) => {
  const { data } = useUserProfile()

  // TODO: `cloud-redesign-mvp` need to be enabled by default in order to have
  // the Prism changes in anonymous pages like Sing In and Forgot Password.
  // This will be removed when removing 'cloud-redesign-mvp' feature flag
  // https://github.com/sketch-hq/Cloud/issues/9158
  const featureFlags = (data?.me?.featureFlags ?? ['cloud-redesign-mvp']).map(
    f => f as string
  )

  const { localFlagOverrides } = useDevToolsFlags()

  const flags = useMemo(
    () => ({
      ...defaultFlags,
      ...formatArrayIntoObject(featureFlags),
      ...localFlagOverrides,
    }),
    [featureFlags, localFlagOverrides]
  )

  useEffect(() => {
    const sentryFlags = mapValuesToString(flags)

    Sentry.configureScope(scope => {
      scope.setContext('Feature Flags', sentryFlags)
    })
  }, [flags])

  return <FlagsProvider value={flags}>{children}</FlagsProvider>
}

export default ConnectedFlagsProvider
